import { apiSlice } from './apiSlice'

export interface BracketMatch {
  team_plaintiff_id: string
  team_defendant_id: string
  // Phase 1 Match Context (Phase 6 publish-match — 2026-05-25):
  match_id?: string | null
  status?: string | null  // pending | scheduled | in_progress | scored | published
  courtroom_id?: string | null
  winner_team_id?: string | null
  scheduled_at?: string | null
  judge_ids?: string[] | null
  participating_team_ids?: string[] | null
}

export interface BracketRecord {
  id?: string
  case_id: string
  status: string
  matches: BracketMatch[]
  created_at?: string
  created_by?: string
  confirmed_at?: string | null
  confirmed_by?: string | null
  round_id?: string | null
  round_type?: string | null
  eligible_team_ids?: string[]
}

export type RolePosition = 'party' | 'lawyer_lead' | 'lawyer_support_1' | 'lawyer_support_2'

export interface RoleEntry {
  position: RolePosition
  full_name: string
  student_id?: string | null
  member_user_id?: string | null
  notes?: string | null
}

export interface RoleAssignmentSheet {
  id?: string
  case_id: string
  round_id?: string | null
  title: string
  // structured (M05-2) không có file; upload (legacy PDF) có đầy đủ.
  submission_kind?: 'structured' | 'upload' | null
  file_name?: string | null
  storage_path?: string | null
  file_size?: number | null
  mime_type?: string | null
  uploaded_by: string
  uploaded_at?: string
  team_id?: string | null
  uploader_role?: string | null
  roles?: RoleEntry[] | null
  representing_role?: 'plaintiff' | 'defendant' | null
  is_late?: boolean | null
  minutes_late?: number | null
  deadline_iso?: string | null
}

export interface StructuredRoleSheetSubmit {
  case_id: string
  round_id: string
  team_id: string
  representing_role: 'plaintiff' | 'defendant'
  roles: RoleEntry[]
  title?: string
}

export interface RoundSheetResponse {
  sheet: RoleAssignmentSheet | null
  deadline_iso: string | null
  penalty: {
    is_late: boolean | null
    minutes_late: number | null
    penalty_points: number
    sheet_id: string | null
    submitted: boolean
  }
}

export const bracketsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    drawBracket: builder.mutation<
      BracketRecord,
      { case_id: string; round_id?: string; eligible_team_ids?: string[] }
    >({
      query: (body) => ({ url: '/brackets/draw', method: 'POST', body }),
      invalidatesTags: ['Bracket', 'Case'],
    }),
    getLatestBracket: builder.query<
      BracketRecord | null,
      string | { case_id: string; round_id?: string }
    >({
      query: (arg) => {
        // Backward-compat: chấp nhận string (case_id) hoặc object {case_id, round_id}.
        const caseId = typeof arg === 'string' ? arg : arg.case_id
        const roundId = typeof arg === 'string' ? undefined : arg.round_id
        return {
          url: '/brackets/latest',
          params: roundId ? { case_id: caseId, round_id: roundId } : { case_id: caseId },
        }
      },
      providesTags: (result, err, arg) => {
        const caseId = typeof arg === 'string' ? arg : arg.case_id
        const roundId = typeof arg === 'string' ? '' : arg.round_id || ''
        return [{ type: 'Bracket', id: `${caseId}:${roundId}` }]
      },
    }),
    deleteBracket: builder.mutation<{ deleted: boolean; bracket_id: string }, string>({
      query: (bracketId) => ({
        url: `/brackets/${bracketId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Bracket'],
    }),
    confirmBracket: builder.mutation<BracketRecord, string>({
      query: (bracketId) => ({
        url: `/brackets/${bracketId}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: ['Bracket', 'Case'],
    }),
    // 2026-05-25 (Issue 2): configure 1 match — set time + judges + meeting link
    // + tự gửi thông báo cho 2 đội + judges.
    configureMatch: builder.mutation<
      {
        match: any
        courtroom_id?: string | null
        meeting_link?: string | null
        judge_assignment_ids: Array<string | null>
        notified_user_ids: string[]
        notified_count: number
      },
      {
        bracket_id: string
        match_id: string
        scheduled_at?: string | null
        judge_ids?: string[] | null
        meeting_link?: string | null
        auto_create_daily?: boolean
        notify?: boolean
      }
    >({
      query: (body) => ({
        url: '/brackets/match/configure',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Bracket', 'Case'],
    }),
    listRoleSheets: builder.query<RoleAssignmentSheet[], string>({
      query: (caseId) => ({
        url: '/role-assignment-sheets/list',
        params: { case_id: caseId },
      }),
      providesTags: (res, err, caseId) => [{ type: 'RoleSheet', id: caseId }],
    }),
    uploadRoleSheet: builder.mutation<
      RoleAssignmentSheet,
      { caseId: string; title?: string; file: File }
    >({
      query: ({ caseId, title, file }) => {
        const fd = new FormData()
        fd.append('case_id', caseId)
        if (title) fd.append('title', title)
        fd.append('file', file)
        return {
          url: '/role-assignment-sheets/upload',
          method: 'POST',
          body: fd,
        }
      },
      invalidatesTags: (res, err, { caseId }) => [{ type: 'RoleSheet', id: caseId }],
    }),
    deleteRoleSheet: builder.mutation<{ message: string }, string>({
      query: (sheetId) => ({
        url: `/role-assignment-sheets/${sheetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RoleSheet'],
    }),
    downloadRoleSheet: builder.query<Blob, string>({
      query: (sheetId) => ({
        url: `/role-assignment-sheets/${sheetId}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    submitStructuredRoleSheet: builder.mutation<RoleAssignmentSheet, StructuredRoleSheetSubmit>({
      query: (body) => ({
        url: '/role-assignment-sheets/structured',
        method: 'POST',
        body,
      }),
      invalidatesTags: (res, err, body) => [
        { type: 'RoleSheet', id: body.case_id },
        'RoleSheet',
      ],
    }),
    getTeamRoundSheet: builder.query<
      RoundSheetResponse,
      { case_id: string; team_id: string; round_id: string }
    >({
      query: (params) => ({
        url: '/role-assignment-sheets/round-sheet',
        params,
      }),
      providesTags: (res, err, args) => [
        { type: 'RoleSheet', id: `${args.case_id}:${args.team_id}:${args.round_id}` },
      ],
    }),
    getRoleSheetDeadline: builder.query<
      { round_id: string; deadline_iso: string | null; offset_hours: number },
      string
    >({
      query: (roundId) => ({
        url: '/role-assignment-sheets/round-deadline',
        params: { round_id: roundId },
      }),
    }),
  }),
})

export const {
  useDrawBracketMutation,
  useGetLatestBracketQuery,
  useConfirmBracketMutation,
  useDeleteBracketMutation,
  useConfigureMatchMutation,
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
  useSubmitStructuredRoleSheetMutation,
  useGetTeamRoundSheetQuery,
  useGetRoleSheetDeadlineQuery,
} = bracketsApi
